/**
 * Sound cue dispatcher for the table UI. Each named cue maps to an
 * optional audio file under `/sounds/<name>.mp3`. If the file is
 * missing (default ship state — operators drop their own assets
 * in) the playback silently no-ops so the UI never breaks on a
 * missing cue.
 *
 * Mute + volume preferences live on `localStorage` under
 * `arkhe.sound.muted` + `arkhe.sound.volume` so the settings
 * survive across sessions. The HUD menu's settings panel reads /
 * writes via `useUIStore`, which mirrors back to localStorage on
 * every flip.
 *
 * `prefers-reduced-motion` is honoured as an *implicit* mute hint
 * — users who disable motion typically also disable audio cues —
 * but the explicit mute toggle always wins (a user can re-enable
 * sound even with reduced-motion on).
 *
 * **Cue catalogue** — each one is fired by `ws/sound_cue.ts`, which
 * owns the event → cue mapping and is where the gating lives.
 *   - `card_deal`  — `HandStarted` (hole cards) + flop / turn / river
 *   - `fold` / `check` / `call` / `bet` / `raise` / `all_in`
 *                  — `ActionApplied`, one per kind the wire has
 *   - `your_turn`  — `TimerArmed` on the caller's OWN seat
 *   - `win`        — `Settled` where the caller took a share of the pot
 *
 * The six action kinds used to share one `chip_drag` cue for the four
 * that move chips, and `check` had no cue at all. So a table that
 * sounded its folds sounded nothing for a check, a call or an all-in —
 * the player heard the room go quiet at the moments it should have
 * been loudest. One cue per kind means an operator can hear the
 * difference, and the mapping is exhaustive over the wire's own union
 * so a new action kind cannot ship silent.
 *
 * Four more were declared once and never played: a board-reveal cue, a
 * chip-stack rebuild, a winner cheer, a UI tap. They are gone. A cue
 * nobody fires is worse than an absent one — an operator drops an
 * asset in for it, hears nothing, and has no way to tell a missing
 * wire from a missing file, because both are silent by design.
 */

import {
  clampVolume,
  DEFAULT_AUDIO_VOLUME,
  installAudioUnlock,
  playAudioFile,
  type AudioPlayBackend,
  setAudioPlayBackend,
} from "./audio_player";

/// The cues the client fires. Nothing else belongs here: a cue that
/// no caller plays is a filename nobody can act on — an operator
/// drops an asset in for it and hears silence, with no way to tell a
/// missing wire from a missing file.
export type SoundCue =
  | "card_deal"
  | "fold"
  | "check"
  | "call"
  | "bet"
  | "raise"
  | "all_in"
  | "your_turn"
  | "win";

/// The action cues are listed in the wire's own order so the two
/// unions can be read side by side.
export const SOUND_CUES: ReadonlyArray<SoundCue> = [
  "card_deal",
  "fold",
  "check",
  "call",
  "bet",
  "raise",
  "all_in",
  "your_turn",
  "win",
];

const MUTE_STORAGE_KEY = "arkhe.sound.muted";
const VOLUME_STORAGE_KEY = "arkhe.sound.volume";

/**
 * Read the persisted mute state on first import. Falls back to
 * `false` (sound on) when localStorage is unavailable (SSR /
 * test environments) — the explicit user toggle still wins at
 * runtime.
 */
export function readPersistedMute(): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(MUTE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function writePersistedMute(muted: boolean): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(MUTE_STORAGE_KEY, muted ? "1" : "0");
  } catch {
    // Swallow — quota / privacy mode failures are non-fatal.
  }
}

/**
 * Read the persisted volume preference on first import. Falls back
 * to `DEFAULT_AUDIO_VOLUME` when localStorage is unavailable or
 * carries a non-numeric value.
 */
export function readPersistedVolume(): number {
  try {
    if (typeof localStorage === "undefined") return DEFAULT_AUDIO_VOLUME;
    const raw = localStorage.getItem(VOLUME_STORAGE_KEY);
    if (raw === null) return DEFAULT_AUDIO_VOLUME;
    const parsed = Number.parseFloat(raw);
    return clampVolume(parsed);
  } catch {
    return DEFAULT_AUDIO_VOLUME;
  }
}

export function writePersistedVolume(volume: number): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(VOLUME_STORAGE_KEY, String(clampVolume(volume)));
  } catch {
    // Non-fatal.
  }
}

/**
 * Pluggable audio backend (re-exported from `audio_player`) so
 * existing imports (`import type { SoundBackend } from
 * "../lib/sound"`) continue to compile. Tests should prefer the
 * `setAudioPlayBackend` seam exported below.
 */
export type SoundBackend = AudioPlayBackend;

/** Test seam — swap the backend out for a stub. Returns the
 *  previous backend so the caller can restore it. */
export function setSoundBackend(b: SoundBackend): SoundBackend {
  return setAudioPlayBackend(b);
}

/**
 * Play a cue. Caller passes the current mute state (typically
 * sourced from `useUIStore.getState().soundMuted`) + an optional
 * volume override. The default volume falls through to the
 * persisted preference so a caller that omits it gets the
 * operator-tuned level rather than the module default.
 */
export function playSound(
  cue: SoundCue,
  options: { muted: boolean; volume?: number } = { muted: false },
): void {
  if (options.muted) return;
  const url = soundUrl(cue);
  const volume = options.volume ?? readPersistedVolume();
  playAudioFile(url, { volume, muted: false });
}

/** Where a cue's file is served. */
export function soundUrl(cue: SoundCue): string {
  return `/sounds/${cue}.mp3`;
}

/** Let the first touch start the audio, and fetch every cue then. */
export function installSoundUnlock(): void {
  installAudioUnlock(SOUND_CUES.map(soundUrl));
}
